import {AdminDeliveryBundles} from '@/components/AdminDeliveryBundles';
import {RunJobActions,RunContactActions,RunHistory,runStatusLabel,runStatusClass} from "@/components/RunOrderCard";
import { customerItemTotal } from "../../../../lib/ordering-pricing";
import { ChatImageComposer, MessageImages } from "@/components/MessageImages";
import {postAdminOrderRefund} from "@/lib/adminRefund";
import { useEffect, useState, useCallback, useRef } from "react";
import { AdminLayout } from "@/components/layout/AdminLayout";
import {
  Clock, Package, Truck, AlertTriangle, CheckCircle2,
  RefreshCw, RotateCcw, Search, ChevronLeft, ChevronRight,
  XCircle, UserPlus, ChevronDown, CheckCheck, Zap, Layers, MapPin,
  UserMinus, MessageCircle, Send, History, PhoneCall, Eye, Banknote,
} from "lucide-react";
import DriverLocationMap from "@/components/DriverLocationMap";
import OrderTimeline, { type OrderTimelineData, type TimelineState } from "@/components/OrderTimeline";

function authHeaders(): Record<string, string> {
  const token = localStorage.getItem("ayrshire_token");
  return token
    ? { "Content-Type": "application/json", Authorization: `Bearer ${token}` }
    : { "Content-Type": "application/json" };
}

type ParsedOption = { groupName?: string; optionName?: string; name?: string };
type OptionGroup = { groupName: string; labels: string[] };

function parseOptionGroups(raw: string | null): OptionGroup[] {
  if (!raw) return [];
  try {
    const opts = JSON.parse(raw) as ParsedOption[];
    const groups: OptionGroup[] = [];
    for (const o of opts) {
      const gn = o.groupName ?? "";
      const label = o.optionName ?? o.name ?? "";
      const last = groups[groups.length - 1];
      if (last && last.groupName === gn) last.labels.push(label);
      else groups.push({ groupName: gn, labels: [label] });
    }
    return groups;
  } catch { return []; }
}

type Order = {
  id: number;
  customerId: number | null;
  storeId: number;
  storeName: string | null;
  storeOwnerId: number | null;
  status: string;
  statusChangedAt: string | null;
  createdAt: string;
  totalPence: number;
  subtotalPence: number;
  deliveryFeePence: number;
  leg2DeliveryFeePence: number | null;
  serviceChargePence: number | null;
  bagChargePence: number | null;
  offerDiscountPence: number;
  offerTitle: string | null;
  customerPlatformFeePence: number;
  customerUpliftMarginPence?: number | null;
  customerHubDriverFeePence: number;
  kindnessCreditPence?: number | null;
  kindnessContributionPence?: number | null;
  kindnessDonorName?: string | null;
  customerPaidPence: number | null;
  storeGrossProceedsPence: number | null;
  driverFuelSupplementPence: number | null;
  hubFundedDriverPence: number | null;
  driverOfferPence: number;
  platformFeeOwedPence: number | null;
  platformFeeSettledAt: string | null;
  orderSource: string;
  externalReference: string | null;
  paymentMethod: string;
  refundedPence: number | null;
  refundedAt: string | null;
  stripePaymentIntentId: string | null;
  fulfilmentType: string;
  driverMode: string | null;
  dispatchStatus: string | null;
  deliveryCode: string | null;
  customerName: string | null;
  customerPhone: string | null;
  /* Customer history, matched on user id or phone so guest checkout churn
     does not make a regular look new every time. */
  customerOrderCount?: number | null;
  customerPreviousOrders?: number | null;
  customerTotalSpentPence?: number | null;
  customerIsReturning?: boolean | null;
  /* How many times the system auto-phoned the shop about this unaccepted order. */
  shopCallAttempts?: number;
  shopCallBackupCalled?: boolean;
  deliveryAddress: string | null;
  notes: string | null;
  assignmentId: number | null;
  driverId: number | null;
  driverName: string | null;
  deliveryPhotoUrl: string | null;
  claimedAt: string | null;
  pickedUpAt: string | null;
  deliveredAt: string | null;
  driverEarningsPence: number | null;
  driverBonusPence: number | null;
  driverTipPence: number | null;
  scheduledFor: string | null;
  multiOrderId: number | null;
  isMultiOrderChild: boolean;
  multiOrderSiblings: Array<{ id: number; storeName: string | null; status: string }>;
  multiOrderDeliveryFeePence: number | null;
  storeLatitude: number | null;
  storeLongitude: number | null;
  items: Array<{ id: number; name: string; quantity: number; pricePence: number; lineTotalPence: number | null; isFreeItem: boolean; notes?: string | null; selectedOptions: string | null }>;
  ledgerTransactions: Array<{ id: number; kind: string; status: string; stripeSessionId: string | null; settledAt: string | null; entries: Array<{ accountType: string; component: string; direction: string; amountPence: number }> }>;
};

/** One row of a customer's previous-order list, from
 *  GET /admin/orders/:id/customer-history. */
type CustomerHistoryOrder = {
  id: number;
  createdAt: string;
  status: string;
  storeName: string | null;
  fulfilmentType: string | null;
  /** What the customer paid, fees included — not the merchant total. */
  customerPaidPence: number;
};

type Tab = "live" | "completed" | "cancelled" | "all";

const STATUS_LABEL: Record<string, string> = {
  pending: "Pending",
  confirmed: "Confirmed",
  preparing: "Preparing",
  ready: "Ready",
  out_for_delivery: "Out for Delivery",
  delivered: "Delivered",
  cancelled: "Cancelled",
};

const STATUS_COLOR: Record<string, string> = {
  pending: "bg-slate-100 text-slate-700",
  confirmed: "bg-blue-100 text-blue-700",
  preparing: "bg-amber-100 text-amber-700",
  ready: "bg-orange-100 text-orange-700",
  out_for_delivery: "bg-green-100 text-green-700",
  delivered: "bg-emerald-100 text-emerald-700",
  cancelled: "bg-red-100 text-red-700",
};

function elapsed(isoString: string | null | undefined): { mins: number; label: string } {
  if (!isoString) return { mins: 0, label: "–" };
  const mins = Math.floor((Date.now() - new Date(isoString).getTime()) / 60000);
  if (mins < 1) return { mins: 0, label: "< 1 min" };
  if (mins < 60) return { mins, label: `${mins} min${mins === 1 ? "" : "s"}` };
  const h = Math.floor(mins / 60);
  const m = mins % 60;
  return { mins, label: `${h}h ${m}m` };
}

function TimerBadge({ isoString, label }: { isoString: string | null | undefined; label: string }) {
  const { mins, label: elapsedLabel } = elapsed(isoString);
  const color =
    mins >= 20 ? "bg-red-100 text-red-700 border-red-200" :
    mins >= 10 ? "bg-amber-100 text-amber-700 border-amber-200" :
    "bg-emerald-50 text-emerald-700 border-emerald-200";
  return (
    <div className={`flex items-center gap-1.5 text-xs font-medium px-2.5 py-1 rounded-full border ${color}`}>
      <Clock size={11} />
      <span>{label}: {elapsedLabel}</span>
    </div>
  );
}

function pence(p: number) {
  return `£${(p / 100).toFixed(2)}`;
}

function fmtDateTime(iso: string) {
  return new Date(iso).toLocaleString("en-GB", {
    day: "numeric", month: "short", year: "numeric",
    hour: "2-digit", minute: "2-digit",
  });
}

type RefundModal = {
  requestId: string;
  order: Order;
  amountInput: string;
  mode: "full" | "partial";
  busy: boolean;
  error: string | null;
  success: string | null;
};

type Driver = { userId: number; userName: string | null; isOnline: boolean };

type AssignModal = {
  order: Order;
  selectedDriverId: number | null;
  busy: boolean;
  error: string | null;
};

type ChatMessage = {
  id: number;
  senderUserId: number;
  recipientUserId: number | null;
  senderName: string | null;
  body: string; attachmentIds?: string[];
  createdAt: string;
};

/** Admins can read every thread on an order, so the per-party chat must be scoped
 *  client-side to just the messages to/from the party whose tab is open —
 *  otherwise a driver message shows up in the customer tab and vice versa. */
function scopeThread(items: ChatMessage[] | undefined, partyUserId: number | null): ChatMessage[] {
  const list = Array.isArray(items) ? items : [];
  if (partyUserId == null) return list;
  return list.filter((m) => m.senderUserId === partyUserId || m.recipientUserId === partyUserId);
}

type ChatTarget = "customer" | "driver" | "shop";

type ChatModal = {
  order: Order;
  target: ChatTarget;
  recipientUserId: number | null;
  title: string;
  messages: ChatMessage[];
  input: string;
  busy: boolean;
  sending: boolean;
  error: string | null;
};

type ShopMessage = { id: number; senderRole: string; body: string; attachmentIds?: string[]; sentAt: string };

type SpyModal = {
  order: Order;
  driver: ChatMessage[];
  shop: ShopMessage[];
  busy: boolean;
  error: string | null;
};

const LIVE_STATUSES = ["pending", "confirmed", "preparing", "ready", "out_for_delivery"] as const;

// Module-level (NOT nested inside AdminOrders) so its component identity is stable.
// When it was declared inside the parent, every 15s order-poll re-render recreated
// the function → React remounted the whole Leaflet map → "map keeps refreshing" and
// follow/zoom/pan reset on every tick. Hoisting it fixes that; the map now persists
// and only pans when the driver actually moves.
function DriverTrackingPanel({ orderId, storeLat, storeLng }: { orderId: number; storeLat: number | null; storeLng: number | null }) {
  const [driverPos, setDriverPos] = useState<{ lat: number; lng: number } | null>(null);
  const [noLocation, setNoLocation] = useState(false);
  const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null);

  const poll = useCallback(async () => {
    try {
      const r = await fetch(`/api/orders/${orderId}/driver`, { headers: authHeaders() });
      if (!r.ok) return;
      const d = await r.json() as { assignment: { driverLat: number | null; driverLng: number | null } | null };
      const lat = d.assignment?.driverLat ?? null;
      const lng = d.assignment?.driverLng ?? null;
      if (lat != null && lng != null) {
        // Only update when the position actually changed — returning the same
        // object reference makes React bail out of the re-render, so the map
        // never redraws unless the driver has genuinely moved.
        setDriverPos(prev => (prev && prev.lat === lat && prev.lng === lng) ? prev : { lat, lng });
        setNoLocation(false);
      } else setNoLocation(true);
    } catch { /* ignore */ }
  }, [orderId]);

  useEffect(() => {
    void poll();
    intervalRef.current = setInterval(() => void poll(), 15000);
    return () => { if (intervalRef.current) clearInterval(intervalRef.current); };
  }, [poll]);

  if (noLocation && !driverPos) {
    return (
      <div className="flex items-center gap-2 bg-slate-50 border border-slate-200 rounded-xl px-3 py-3 text-xs text-slate-500">
        <span className="w-2 h-2 rounded-full bg-slate-300 animate-pulse flex-shrink-0" />
        Waiting for driver location…
      </div>
    );
  }
  if (!driverPos) return null;
  return (
    <div className="rounded-xl overflow-hidden border border-slate-200">
      <DriverLocationMap
        lat={driverPos.lat}
        lng={driverPos.lng}
        storeLat={storeLat}
        storeLng={storeLng}
        height={220}
      />
    </div>
  );
}

export default function AdminOrders() {
  const [tab, setTab] = useState<Tab>("live");
  const [orders, setOrders] = useState<Order[]>([]);
  const [runs, setRuns] = useState<Array<{ id: number; status: string; storeName: string | null; driverName: string | null; dropCount: number; deliveredCount: number; dropFeesTotalPence: number; fuelSupplementPence: number; hubFeePence: number; runNote: string | null; createdAt: string; completedAt?: string | null; stops?: Array<{ id: number; sequence: number; areaLabel: string | null; addressText: string | null; postcode: string | null; status: string; feePence: number; deliveryPhotoUrl: string | null; deliveredLat?: number | null; deliveredLng?: number | null; deliveredAt?: string | null }> }>>([]);
  const [expandedRunIds, setExpandedRunIds] = useState<Set<number>>(new Set());
  const [total, setTotal] = useState(0);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(true);
  const [lastRefresh, setLastRefresh] = useState(new Date());
  const [, setTick] = useState(0);
  const [search, setSearch] = useState("");
  const [searchInput, setSearchInput] = useState("");
  const [refundModal, setRefundModal] = useState<RefundModal | null>(null);
  const [assignModal, setAssignModal] = useState<AssignModal | null>(null);
  const [drivers, setDrivers] = useState<Driver[]>([]);
  const [cancellingId, setCancellingId] = useState<number | null>(null);
  const [advancingId, setAdvancingId] = useState<number | null>(null);
  const [redispatchingId, setRedispatchingId] = useState<number | null>(null);
  const [forcePickupId, setForcePickupId] = useState<number | null>(null);
  const [unassigningId, setUnassigningId] = useState<number | null>(null);
  const [chatModal, setChatModal] = useState<ChatModal | null>(null);
  const [spyModal, setSpyModal] = useState<SpyModal | null>(null);
  const bumpInFlight = useRef(false);
  const [bumpingId, setBumpingId] = useState<number | null>(null);
  const [expandedIds, setExpandedIds] = useState<Set<number>>(new Set());
  /* Customer history panel: which order's badge is open, and what we loaded. */
  const [historyFor, setHistoryFor] = useState<number | null>(null);
  const [histories, setHistories] = useState<Record<number, { loading: boolean; orders?: CustomerHistoryOrder[]; error?: string }>>({});
  const [trackingIds, setTrackingIds] = useState<Set<number>>(new Set());
  // Audit trails are opened one card at a time and cached for the life of the
  // page. A page of 50 orders would be 50 multi-table reads if we prefetched,
  // and the trail of a step that already happened cannot change under us.
  const [timelineIds, setTimelineIds] = useState<Set<number>>(new Set());
  const [timelines, setTimelines] = useState<Record<number, TimelineState>>({});
  const chatBottomRef = useRef<HTMLDivElement>(null);
  const latestRequestRef = useRef(0);
  const latestLoadingRequestRef = useRef(0);
  const [orderStats, setOrderStats] = useState<{
    today: { total: number; cancelled: number };
    thisWeek: { total: number; cancelled: number };
    lastWeek: { total: number; cancelled: number };
  } | null>(null);

  const PAGE_SIZE = 50;

  const fetchOrderStats = useCallback(async () => {
    try {
      const r = await fetch("/api/admin/orders/stats", { headers: authHeaders() });
      if (r.ok) setOrderStats(await r.json() as typeof orderStats);
    } catch { /* non-critical */ }
  }, []);

  const fetchOrders = useCallback(async (t: Tab, p: number, s: string, background = false) => {
    const requestId = ++latestRequestRef.current;
    if (!background) {
      latestLoadingRequestRef.current = requestId;
      setLoading(true);
      setLoadError(null);
    }
    try {
      const params = new URLSearchParams({ tab: t, page: String(p) });
      if (s) params.set("search", s);
      const r = await fetch(`/api/admin/orders?${params}`, { headers: authHeaders() });
      if (!r.ok) throw new Error("Could not load orders");
      const data = await r.json() as { items?: Order[]; total?: number; runs?: typeof runs };
      if (requestId !== latestRequestRef.current) return;
      const responseOrders = Array.isArray(data.items) ? data.items : [];
      const matchingOrders = responseOrders.filter((order) =>
        t === "completed" ? order.status === "delivered"
        : t === "cancelled" ? order.status === "cancelled"
        : t === "live" ? !["delivered", "cancelled"].includes(order.status)
        : true
      );
      const nextOrders = matchingOrders.map((order) => ({
        ...order,
        items: Array.isArray(order.items) ? order.items : [],
        multiOrderSiblings: Array.isArray(order.multiOrderSiblings) ? order.multiOrderSiblings : [],
        multiOrderDeliveryFeePence: order.multiOrderDeliveryFeePence ?? null,
        ledgerTransactions: Array.isArray(order.ledgerTransactions)
          ? order.ledgerTransactions.map((transaction) => ({
              ...transaction,
              entries: Array.isArray(transaction.entries) ? transaction.entries : [],
            }))
          : [],
      }));
      setOrders(nextOrders);
      setRuns(Array.isArray(data.runs) ? data.runs : []);
      setTotal(typeof data.total === "number" ? data.total : nextOrders.length);
      setLoadError(null);
      setLastRefresh(new Date());
    } catch (error) {
      if (requestId !== latestRequestRef.current) return;
      if (!background) {
        setOrders([]);
        setTotal(0);
        setLoadError(error instanceof Error ? error.message : "Could not load orders");
      }
    } finally {
      if (!background && requestId === latestLoadingRequestRef.current) setLoading(false);
    }
  }, []);

  useEffect(() => {
    void fetchOrders(tab, page, search);
  }, [tab, page, search, fetchOrders]);

  useEffect(() => { void fetchOrderStats(); }, [fetchOrderStats]);

  // Load driver list once for assign modal
  useEffect(() => {
    void (async () => {
      const r = await fetch("/api/admin/drivers", { headers: authHeaders() });
      if (r.ok) {
        const data = await r.json() as { items: Driver[] };
        setDrivers(data.items);
      }
    })();
  }, []);

  /* One fetch per order, cached — the panel can be reopened without re-hitting
     the API, and two customers on screen do not clobber each other. */
  const loadCustomerHistory = useCallback(async (orderId: number) => {
    setHistories((prev) => (prev[orderId]?.orders ? prev : { ...prev, [orderId]: { loading: true } }));
    try {
      const r = await fetch(`/api/admin/orders/${orderId}/customer-history`, { headers: authHeaders() });
      const data = await r.json() as { orders?: CustomerHistoryOrder[]; error?: string };
      if (!r.ok) throw new Error(data.error ?? "Could not load history");
      setHistories((prev) => ({ ...prev, [orderId]: { loading: false, orders: data.orders ?? [] } }));
    } catch (e) {
      setHistories((prev) => ({ ...prev, [orderId]: { loading: false, error: e instanceof Error ? e.message : "Failed" } }));
    }
  }, []);

  /* Jump to one specific order: widen to every tab, then search its id. */
  const openOrderById = useCallback((id: number) => {
    setHistoryFor(null);
    setTab("all");
    setSearchInput(String(id));
    setSearch(String(id));
    setExpandedIds(new Set([id]));
  }, []);

  const loadTimeline = useCallback(async (orderId: number) => {
    setTimelines(prev => ({ ...prev, [orderId]: { phase: "loading" } }));
    try {
      const r = await fetch(`/api/admin/orders/${orderId}/timeline`, { headers: authHeaders() });
      const data = await r.json() as Partial<OrderTimelineData> & { error?: string };
      if (!r.ok) throw new Error(data.error ?? "Could not load the audit trail");
      setTimelines(prev => ({
        ...prev,
        [orderId]: {
          phase: "ready",
          data: {
            orderId,
            storeName: data.storeName ?? null,
            status: data.status ?? "",
            placedAt: data.placedAt ?? new Date().toISOString(),
            totalMinutes: data.totalMinutes ?? null,
            entries: Array.isArray(data.entries) ? data.entries : [],
          },
        },
      }));
    } catch (err) {
      setTimelines(prev => ({ ...prev, [orderId]: { phase: "error", message: (err as Error).message } }));
    }
  }, []);

  /**
   * A delivered or cancelled order can never gain another entry, so its trail is
   * cached for good. A live one is the whole reason this page exists — an admin
   * watching an order unfold must not be shown a stale snapshot from the last
   * time they looked — so re-opening it re-reads. Still nothing on page load,
   * and an in-flight request is never doubled up.
   */
  const toggleTimeline = (orderId: number) => {
    const opening = !timelineIds.has(orderId);
    setTimelineIds(prev => {
      const next = new Set(prev);
      if (opening) next.add(orderId); else next.delete(orderId);
      return next;
    });
    if (!opening) return;

    const cached = timelines[orderId];
    const order = orders.find(o => o.id === orderId);
    const settled = order?.status === "delivered" || order?.status === "cancelled";
    const worthRefetching = !cached || (!settled && cached.phase !== "loading");
    if (worthRefetching) void loadTimeline(orderId);
  };

  const redispatch = async (orderId: number) => {
    setRedispatchingId(orderId);
    try {
      const r = await fetch(`/api/admin/orders/${orderId}/redispatch`, { method: "POST", headers: authHeaders() });
      const data = await r.json() as { error?: string };
      if (!r.ok) throw new Error(data.error ?? "Redispatch failed");
      setOrders(prev => prev.map(o => o.id === orderId ? { ...o, dispatchStatus: "dispatching" } : o));
    } catch (err) {
      alert((err as Error).message);
    } finally {
      setRedispatchingId(null);
    }
  };

  const forcePickup = async (orderId: number) => {
    if (!confirm(`Force-mark order #${orderId} as picked up? Use when the driver has the order but the app is blocking them (e.g. past the store geofence).`)) return;
    setForcePickupId(orderId);
    try {
      const r = await fetch(`/api/admin/orders/${orderId}/force-pickup`, { method: "POST", headers: authHeaders() });
      const data = await r.json() as { error?: string };
      if (!r.ok) throw new Error(data.error ?? "Force pickup failed");
      setOrders(prev => prev.map(o => o.id === orderId ? { ...o, status: "out_for_delivery" } : o));
    } catch (err) {
      alert((err as Error).message);
    } finally {
      setForcePickupId(null);
    }
  };

  const unassignDriver = async (order: Order) => {
    if (!confirm(`Unassign ${order.driverName ?? "the driver"} from Order #${order.id}? The order will return to Ready and can be re-dispatched.`)) return;
    setUnassigningId(order.id);
    try {
      const r = await fetch(`/api/admin/orders/${order.id}/unassign-driver`, { method: "POST", headers: authHeaders() });
      const data = await r.json() as { error?: string; newStatus?: string };
      if (!r.ok) throw new Error(data.error ?? "Unassign failed");
      setOrders(prev => prev.map(o => o.id === order.id
        ? { ...o, assignmentId: null, driverId: null, driverName: null, status: (data.newStatus ?? o.status) as Order["status"] }
        : o));
    } catch (err) {
      alert((err as Error).message);
    } finally {
      setUnassigningId(null);
    }
  };

  const openChat = async (order: Order, target: ChatTarget) => {
    const recipientUserId = target === "customer" ? order.customerId : target === "driver" ? order.driverId : order.storeOwnerId;
    const who = target === "customer" ? `Customer: ${order.customerName ?? "Unknown"}`
      : target === "driver" ? `Driver: ${order.driverName ?? "Unknown"}`
      : `Shop: ${order.storeName ?? "Unknown"}`;
    setChatModal({ order, target, recipientUserId, title: who, messages: [], input: "", busy: true, sending: false, error: null });
    try {
      const r = await fetch(`/api/orders/${order.id}/driver-messages`, { headers: authHeaders() });
      const data = await r.json() as { items?: ChatMessage[] };
      setChatModal(m => m && { ...m, busy: false, messages: scopeThread(data.items, recipientUserId) });
    } catch {
      setChatModal(m => m && { ...m, busy: false, error: "Failed to load messages" });
    }
  };

  const sendChatMessage = async () => {
    if (!chatModal?.input.trim() || !chatModal.recipientUserId) return;
    const body = chatModal.input.trim();
    const recipientUserId = chatModal.recipientUserId;
    setChatModal(m => m && { ...m, input: "", sending: true, error: null });
    try {
      const r = await fetch(`/api/orders/${chatModal.order.id}/driver-messages`, {
        method: "POST",
        headers: authHeaders(),
        body: JSON.stringify({ body, recipientUserId }),
      });
      const data = await r.json() as { error?: string };
      if (!r.ok) throw new Error(data.error ?? "Send failed");
      const r2 = await fetch(`/api/orders/${chatModal.order.id}/driver-messages`, { headers: authHeaders() });
      const data2 = await r2.json() as { items?: ChatMessage[] };
      setChatModal(m => m && { ...m, sending: false, messages: scopeThread(data2.items, recipientUserId) });
      setTimeout(() => chatBottomRef.current?.scrollIntoView({ behavior: "smooth" }), 50);
    } catch (err) {
      setChatModal(m => m && { ...m, sending: false, error: (err as Error).message });
    }
  };

  // Read-only "spy" — view both existing threads for an order (customer↔driver + customer↔shop).
  const openSpy = async (order: Order) => {
    setSpyModal({ order, driver: [], shop: [], busy: true, error: null });
    try {
      const [dr, sr] = await Promise.all([
        fetch(`/api/orders/${order.id}/driver-messages`, { headers: authHeaders() }).then(r => r.json() as Promise<{ items?: ChatMessage[] }>),
        fetch(`/api/admin/orders/${order.id}/shop-conversation`, { headers: authHeaders() }).then(r => r.json() as Promise<{ messages?: ShopMessage[] }>),
      ]);
      setSpyModal(m => m && { ...m, busy: false, driver: Array.isArray(dr.items) ? dr.items : [], shop: Array.isArray(sr.messages) ? sr.messages : [] });
    } catch {
      setSpyModal(m => m && { ...m, busy: false, error: "Failed to load conversations" });
    }
  };

  // Raise the pay offered to drivers by a fixed step and immediately re-offer (existing endpoint).
  const bumpDriverOffer = async (order: Order, addPence: number) => {
    if (bumpInFlight.current) return;
    bumpInFlight.current = true;
    latestRequestRef.current += 1;
    const current = order.driverOfferPence;
    const newTotal = Math.min(5000, current + addPence);
    setBumpingId(order.id);
    try {
      const r = await fetch(`/api/admin/orders/${order.id}/driver-fee`, {
        method: "POST", headers: authHeaders(), body: JSON.stringify({ totalPence: newTotal }),
      });
      const data = await r.json() as { error?: string; driverFeePence: number; hubFundedDriverPence: number };
      if (!r.ok) throw new Error(data.error ?? "Failed to update offer");
      latestRequestRef.current += 1;
      setOrders(os => os.map(o => o.id === order.id
        ? { ...o, hubFundedDriverPence: data.hubFundedDriverPence, driverOfferPence: data.driverFeePence, driverBonusPence: o.assignmentId ? data.driverFeePence - (o.driverEarningsPence ?? o.deliveryFeePence) : o.driverBonusPence }
        : o));
    } catch (err) {
      alert((err as Error).message);
    } finally {
      bumpInFlight.current = false;
      setBumpingId(null);
    }
  };

  const cancelOrder = async (order: Order) => {
    if (!confirm(`Cancel order #${order.id} from ${order.storeName ?? "this store"}? Card payments will be automatically refunded via Stripe.`)) return;
    setCancellingId(order.id);
    latestRequestRef.current += 1;
    try {
      const r = await fetch(`/api/admin/orders/${order.id}/cancel`, { method: "PATCH", headers: authHeaders() });
      const data = await r.json() as { error?: string };
      if (!r.ok) throw new Error(data.error ?? "Cancel failed");
      setOrders(prev => tab === "live"
        ? prev.filter(o => o.id !== order.id)
        : prev.map(o => o.id === order.id ? { ...o, status: "cancelled" } : o));
      if (tab === "live") setTotal(previous => Math.max(0, previous - 1));
      void fetchOrders(tab, page, search, true);
    } catch (err) {
      alert((err as Error).message);
    } finally {
      setCancellingId(null);
    }
  };

  // Accept a pending order on the shop's behalf. Uses the same endpoint the shop
  // uses (admins are authorised there via getStoreAccess), so it runs the full
  // accept: sets the ready time, stops the alert escalation, notifies the
  // customer, and triggers hub-driver dispatch — nothing bypassed.
  const acceptOrder = async (order: Order) => {
    const minsStr = window.prompt(
      `Accept Order #${order.id} on behalf of ${order.storeName ?? "the store"}?\n\nPrep time in minutes:`,
      "15",
    );
    if (minsStr === null) return;
    const mins = Math.max(1, Math.min(180, parseInt(minsStr, 10) || 15));
    setAdvancingId(order.id);
    try {
      const r = await fetch(`/api/order/orders/${order.id}/status`, {
        method: "PATCH",
        headers: authHeaders(),
        body: JSON.stringify({ status: "confirmed", readyInMins: mins }),
      });
      const data = await r.json() as { error?: string };
      if (!r.ok) throw new Error(data.error ?? "Failed to accept order");
      setOrders(prev => prev.map(o => o.id === order.id ? { ...o, status: "confirmed" } : o));
      void fetchOrders(tab, page, search, true);
    } catch (err) {
      alert((err as Error).message);
    } finally {
      setAdvancingId(null);
    }
  };

  const advanceStatus = async (order: Order, targetStatus: "out_for_delivery" | "delivered") => {
    const label = targetStatus === "delivered" ? "Mark as Completed (Delivered)" : "Mark as Out for Delivery";
    if (!confirm(`${label} for Order #${order.id}?`)) return;
    setAdvancingId(order.id);
    latestRequestRef.current += 1;
    try {
      const r = await fetch(`/api/admin/orders/${order.id}/advance-status`, {
        method: "PATCH",
        headers: authHeaders(),
        body: JSON.stringify({ status: targetStatus }),
      });
      const data = await r.json() as { error?: string };
      if (!r.ok) throw new Error(data.error ?? "Failed to update status");
      if (tab === "live" && targetStatus === "delivered") {
        setOrders(prev => prev.filter(o => o.id !== order.id));
        setTotal(previous => Math.max(0, previous - 1));
      } else {
        setOrders(prev => prev.map(o => o.id === order.id ? { ...o, status: targetStatus } : o));
      }
      void fetchOrders(tab, page, search, true);
    } catch (err) {
      alert((err as Error).message);
    } finally {
      setAdvancingId(null);
    }
  };

  const submitAssign = async () => {
    if (!assignModal?.selectedDriverId) return;
    setAssignModal(m => m && { ...m, busy: true, error: null });
    try {
      const r = await fetch(`/api/admin/orders/${assignModal.order.id}/assign-driver`, {
        method: "POST",
        headers: authHeaders(),
        body: JSON.stringify({ driverId: assignModal.selectedDriverId }),
      });
      const data = await r.json() as { error?: string };
      if (!r.ok) throw new Error(data.error ?? "Assignment failed");
      setOrders(prev => prev.map(o => {
        if (o.id !== assignModal.order.id) return o;
        const d = drivers.find(dr => dr.userId === assignModal.selectedDriverId);
        return { ...o, assignmentId: 1, driverName: d?.userName ?? null };
      }));
      setAssignModal(null);
    } catch (err) {
      setAssignModal(m => m && { ...m, busy: false, error: (err as Error).message });
    }
  };

  // Auto-refresh live tab every 15s
  useEffect(() => {
    if (tab !== "live") return;
    const poll = setInterval(() => void fetchOrders("live", page, search, true), 15000);
    return () => clearInterval(poll);
  }, [tab, page, search, fetchOrders]);

  // Tick every 30s to update elapsed timers
  useEffect(() => {
    const tick = setInterval(() => setTick(t => t + 1), 30000);
    return () => clearInterval(tick);
  }, []);

  function switchTab(t: Tab) {
    if (t === tab) {
      void fetchOrders(tab, page, search);
      return;
    }
    latestRequestRef.current += 1;
    setTab(t);
    setOrders([]);
    setTotal(0);
    setLoading(true);
    setLoadError(null);
    setPage(1);
    setSearch("");
    setSearchInput("");
    setExpandedIds(new Set());
    // Collapse trails with the rest of the card state. The fetched data itself
    // is kept — order ids are stable across tabs, so a re-open is still free.
    setTimelineIds(new Set());
  }

  function handleSearch(e: React.FormEvent) {
    e.preventDefault();
    latestRequestRef.current += 1;
    setSearch(searchInput);
    setPage(1);
  }

  const openRefund = async (order: Order) => {
    setRefundModal({ requestId: crypto.randomUUID(), order, amountInput: "", mode: "full", busy: true, error: null, success: null });
    try {
      const r = await fetch(`/api/admin/orders/${order.id}/refund-info`, { headers: authHeaders() });
      const data = await r.json() as { chargedPence?: number; alreadyRefunded?: number; maxRefundable?: number; error?: string };
      if (!r.ok) throw new Error(data.error ?? "Could not load refund info");
      const maxPence = data.maxRefundable ?? 0;
      setOrders(prev => prev.map(o => o.id === order.id ? { ...o, totalPence: data.chargedPence ?? o.totalPence, refundedPence: data.alreadyRefunded ?? o.refundedPence } : o));
      const patchedOrder = { ...order, totalPence: data.chargedPence ?? order.totalPence, refundedPence: data.alreadyRefunded ?? order.refundedPence };
      setRefundModal({ requestId: crypto.randomUUID(), order: patchedOrder, amountInput: (maxPence / 100).toFixed(2), mode: "full", busy: false, error: null, success: null });
    } catch (err) {
      setRefundModal(m => m && { ...m, busy: false, error: (err as Error).message });
    }
  };

  const submitRefund = async () => {
    if (!refundModal) return;
    const alreadyRefunded = refundModal.order.refundedPence ?? 0;
    const maxPence = refundModal.order.totalPence - alreadyRefunded;
    const input = refundModal.amountInput.trim();
    const parts = /^(\d+)(?:\.(\d{1,2}))?$/.exec(input);
    const amountPence = parts ? Number(parts[1]) * 100 + Number((parts[2] ?? '').padEnd(2,'0')) : NaN;
    if (!Number.isSafeInteger(amountPence) || amountPence <= 0 || amountPence > maxPence) {
      setRefundModal(m => m && { ...m, error: `Enter an amount between £0.01 and £${(maxPence / 100).toFixed(2)}` });
      return;
    }
    setRefundModal(m => m && { ...m, busy: true, error: null });
    try {
      const {response:res,acknowledge}=await postAdminOrderRefund(refundModal.order.id,amountPence,authHeaders());
      const data = await res.json() as { error?: string; totalRefundedPence?: number;success?:boolean;refundId?:string };
      if (!res.ok) throw new Error(data.error ?? "Refund failed");
      if (res.status === 202) {
        setRefundModal(m => m && {...m,busy:false,success:'Refund recorded; awaiting confirmation.'});
        return;
      }
      if(data.success!==true||!data.refundId)throw Error(data.error??'Refund receipt is not confirmed yet.');
      acknowledge();
      setOrders(prev =>
        prev.map(o => o.id === refundModal.order.id ? { ...o, refundedPence: data.totalRefundedPence ?? o.refundedPence } : o)
      );
      setRefundModal(m => m && { ...m, busy: false, success: `Refunded £${(amountPence / 100).toFixed(2)} successfully` });
    } catch (err) {
      setRefundModal(m => m && { ...m, busy: false, error: (err as Error).message });
    }
  };


  function renderOrderCard(order: Order) {
    const inCurrentStatus = elapsed(order.statusChangedAt ?? order.createdAt);
    const isHubDelivery = order.driverMode === "hub" && order.fulfilmentType === "delivery";
    const waitingForDriver = isHubDelivery && order.status === "ready" && !order.assignmentId;
    const urgentWait = waitingForDriver && inCurrentStatus.mins >= 5;
    const isLive = !["delivered", "cancelled"].includes(order.status);
    const fullyRefunded = (order.refundedPence ?? 0) >= order.totalPence;
    const canRefund = order.paymentMethod === "card" && order.stripePaymentIntentId && !fullyRefunded;
    const expanded = expandedIds.has(order.id);
    const timelineOpen = timelineIds.has(order.id);
    // Compute customer paid from components — more reliable than customerPaidPence for multi-order children
    // (multi-order delivery fee lives at the group level, not per child order)
    const componentTotal = order.subtotalPence
      + (order.customerUpliftMarginPence ?? 0) // menu-price uplift the customer pays on top of base prices
      + order.deliveryFeePence
      + (order.leg2DeliveryFeePence ?? 0)
      + (order.serviceChargePence ?? 0)
      + (order.bagChargePence ?? 0)
      + (order.customerPlatformFeePence ?? 0)
      + (order.customerHubDriverFeePence ?? 0)
      + (order.kindnessContributionPence ?? 0)   // paid forward (added to the charge)
      - (order.kindnessCreditPence ?? 0)         // received from a previous donor (reduces the charge)
      - order.offerDiscountPence;
    const customerPaid = order.isMultiOrderChild
      ? componentTotal
      : (order.customerPaidPence ?? componentTotal);
    // Item lines are shown at the uplifted (customer) price, so the promotion line must be the
    // customer-facing discount too — derive it so the breakdown reconciles exactly to Customer paid:
    //   items(uplifted) − discount + non-item charges = customer paid.
    const itemsUpliftedSubtotal = order.items.reduce((s, i) => s + customerItemTotal(i), 0);
    const nonItemCharges = order.deliveryFeePence + (order.leg2DeliveryFeePence ?? 0)
      + (order.serviceChargePence ?? 0) + (order.bagChargePence ?? 0)
      + (order.customerPlatformFeePence ?? 0) + (order.customerHubDriverFeePence ?? 0);
    const kindnessCreditLinePence = order.kindnessCreditPence ?? 0;
    const kindnessContributionLinePence = order.kindnessContributionPence ?? 0;
    // Derive the Promotion line so the WHOLE breakdown reconciles EXACTLY to Customer paid:
    //   items(uplifted) − promo − credit + contribution + non-item charges = customer paid
    // Credit and contribution are shown as their own lines AND are already baked into customerPaid,
    // so they must be in this subtraction. The previous version left them out, so a paid-forward
    // order (e.g. #2962) showed the +£5 contribution as the discount and the lines didn't sum.
    const offerOnlyDiscountPence = Math.max(0,
      itemsUpliftedSubtotal + nonItemCharges + kindnessContributionLinePence - kindnessCreditLinePence - customerPaid);
    const driverPaid = (order.driverEarningsPence ?? 0) + (order.driverBonusPence ?? 0) + (order.driverTipPence ?? 0);
    const hubOrderCollection = (order.customerPlatformFeePence ?? 0) + (order.customerHubDriverFeePence ?? 0) + (order.customerUpliftMarginPence ?? 0) + (isHubDelivery ? (order.deliveryFeePence ?? 0) + (order.leg2DeliveryFeePence ?? 0) + (order.driverFuelSupplementPence ?? order.driverBonusPence ?? 0) * (order.leg2DeliveryFeePence ? 2 : 1) : 0);
    const ledgerTransactions = order.ledgerTransactions ?? [];
    const debtRecovered = ledgerTransactions.filter((t) => t.kind === "shop_debt_collection" && t.status === "settled").flatMap((t) => t.entries ?? []).filter((e) => e.accountType === "shop_receivable" && e.direction === "credit").reduce((sum, e) => sum + e.amountPence, 0);

    return (
      <div
        key={order.id}
        className={`bg-white rounded-2xl border p-4 space-y-3 ${urgentWait ? "border-red-300 shadow-sm shadow-red-100" : "border-slate-100"}`}
      >
        {/* Top row */}
        <div className="flex items-start justify-between gap-3 flex-wrap">
          <div className="flex items-center gap-2 flex-wrap">
            <span className="font-bold text-foreground text-base">#{order.id}</span>
            <span className="font-semibold text-slate-700 text-sm">{order.storeName ?? "Unknown store"}</span>
            <span className={`text-xs font-semibold px-2 py-0.5 rounded-full ${STATUS_COLOR[order.status] ?? "bg-slate-100 text-slate-600"}`}>
              {STATUS_LABEL[order.status] ?? order.status}
            </span>
            {order.fulfilmentType === "delivery" ? (
              <span className="text-xs text-slate-400 flex items-center gap-1"><Truck size={10} /> Delivery</span>
            ) : (
              <span className="text-xs text-slate-400 flex items-center gap-1"><Package size={10} /> Collection</span>
            )}
            {(order.kindnessCreditPence ?? 0) > 0 && (
              <span className="text-xs font-semibold px-2 py-0.5 rounded-full bg-amber-50 text-amber-700 border border-amber-200" title="Random Act of Kindness credit applied (hub-funded; shop still paid in full)">
                💛 Kindness −{pence(order.kindnessCreditPence!)}{order.kindnessDonorName ? ` · ${order.kindnessDonorName}` : ""}
              </span>
            )}
            {(order.kindnessContributionPence ?? 0) > 0 && (
              <span className="text-xs font-semibold px-2 py-0.5 rounded-full bg-amber-50 text-amber-700 border border-amber-200" title="Customer paid it forward into the kindness fund">
                💛 Paid forward +{pence(order.kindnessContributionPence!)}
              </span>
            )}
          </div>
          <div className="flex items-center gap-2">
            <span className="font-bold text-slate-800">{pence(customerPaid)}</span>
            <button
              onClick={() => setExpandedIds((previous) => { const next = new Set(previous); if (next.has(order.id)) next.delete(order.id); else next.add(order.id); return next; })}
              className="p-1.5 rounded-lg border border-slate-200 hover:bg-slate-50"
              title="View full order reconciliation"
            >
              <ChevronDown size={15} className={`transition-transform ${expanded ? "rotate-180" : ""}`} />
            </button>
          </div>
        </div>

        {expanded && (
          <div className="rounded-xl border border-slate-200 bg-slate-50/60 overflow-hidden">
            {/* Order / delivery note — the customer's instructions ("leave at
                door", "Meraki bar"). The API always returned it; the admin view
                simply never rendered it, so notes were invisible to support. */}
            {order.notes && order.notes.trim() && (
              <div className="flex items-start gap-2 bg-amber-50 border-b border-amber-200 px-4 py-2.5 text-sm text-amber-900">
                <span className="shrink-0">📝</span>
                <span className="min-w-0"><span className="font-semibold">Order note: </span>{order.notes}</span>
              </div>
            )}
            <div className="grid lg:grid-cols-3 divide-y lg:divide-y-0 lg:divide-x divide-slate-200">
              <div className="p-4 min-w-0">
                <h4 className="font-bold text-sm mb-3">Customer order</h4>
                <div className="space-y-1.5 text-xs">
                  {order.items.map((item) => {
                    const optGroups = parseOptionGroups(item.selectedOptions);
                    return (
                      <div key={item.id} className="flex justify-between gap-3 items-start">
                        <span className="min-w-0">
                          {item.quantity}x {item.name}{item.isFreeItem ? " (Free promotion)" : ""}
                          {optGroups.length > 0 && (
                            <span className="block pl-3 mt-0.5">
                              {optGroups.map((g) => (
                                <span key={g.groupName} className="text-slate-500 block">
                                  {g.groupName && <span className="text-slate-400">{g.groupName}: </span>}
                                  {g.labels.join(", ")}
                                </span>
                              ))}
                            </span>
                          )}
                          {item.notes && (
                            <span className="block pl-3 mt-0.5 text-amber-700">📝 {item.notes}</span>
                          )}
                        </span>
                        <strong className="shrink-0">{pence(customerItemTotal(item))}</strong>
                      </div>
                    );
                  })}
                  <div className="border-t pt-1.5 flex justify-between"><span>Items subtotal</span><strong>{pence(itemsUpliftedSubtotal)}</strong></div>
                  {offerOnlyDiscountPence > 0 && <div className="flex justify-between text-emerald-700"><span>Promotion{order.offerTitle ? `: ${order.offerTitle}` : ""}</span><strong>-{pence(offerOnlyDiscountPence)}</strong></div>}
                  {kindnessCreditLinePence > 0 && <div className="flex justify-between text-amber-600"><span>💛 Random Act of Kindness{order.kindnessDonorName ? ` · ${order.kindnessDonorName}` : ""}</span><strong>-{pence(kindnessCreditLinePence)}</strong></div>}
                  {kindnessContributionLinePence > 0 && <div className="flex justify-between text-amber-600"><span>💛 Paid forward</span><strong>+{pence(kindnessContributionLinePence)}</strong></div>}
                  {order.deliveryFeePence > 0 && <div className="flex justify-between"><span>Delivery</span><strong>{pence(order.deliveryFeePence)}</strong></div>}
                  {(order.leg2DeliveryFeePence ?? 0) > 0 && <div className="flex justify-between"><span>Return delivery</span><strong>{pence(order.leg2DeliveryFeePence!)}</strong></div>}
                  {(order.serviceChargePence ?? 0) > 0 && <div className="flex justify-between"><span>Shop service charge</span><strong>{pence(order.serviceChargePence!)}</strong></div>}
                  {(order.bagChargePence ?? 0) > 0 && <div className="flex justify-between"><span>Carrier bag charge</span><strong>{pence(order.bagChargePence!)}</strong></div>}
                  {((order.customerPlatformFeePence ?? 0) + (order.customerHubDriverFeePence ?? 0)) > 0 && <div className="flex justify-between"><span>Service fee</span><strong>{pence((order.customerPlatformFeePence ?? 0) + (order.customerHubDriverFeePence ?? 0))}</strong></div>}
                  <div className="border-t pt-1.5 flex justify-between text-sm"><span className="font-bold">Customer paid</span><strong>{pence(customerPaid)}</strong></div>
                  {(order.refundedPence ?? 0) > 0 && <div className="flex justify-between text-amber-700"><span>Refunded</span><strong>-{pence(order.refundedPence!)}</strong></div>}
                </div>
              </div>
              <div className="p-4 min-w-0">
                <h4 className="font-bold text-sm mb-3">Ayrshire Hub & driver</h4>
                <div className="space-y-1.5 text-xs">
                  <div className="flex justify-between"><span>Hub collected on this order</span><strong>{pence(hubOrderCollection)}</strong></div>
                  {isHubDelivery && <>
                    <div className="flex justify-between"><span>Driver base pay</span><strong>{pence(order.driverEarningsPence ?? 0)}</strong></div>
                    <div className="flex justify-between"><span>Driver fuel/bonus</span><strong>{pence(order.driverBonusPence ?? order.driverFuelSupplementPence ?? 0)}</strong></div>
                    {(order.hubFundedDriverPence ?? 0) > 0 && <div className="text-xs text-slate-500">Includes {pence(order.hubFundedDriverPence!)} extra paid by Ayrshire Hub.</div>}
                    <div className="flex justify-between"><span>Confirmed driver tip</span><strong>{pence(order.driverTipPence ?? 0)}</strong></div>
                    <div className="border-t pt-1.5 flex justify-between"><span>Total driver earnings</span><strong>{pence(driverPaid)}</strong></div>
                  </>}
                  {order.platformFeeOwedPence != null && <div className="flex justify-between"><span>Cash platform fee owed by shop</span><strong>{pence(order.platformFeeOwedPence)}</strong></div>}
                  <div className="flex justify-between text-emerald-700"><span>Previous ledger debt recovered</span><strong>{pence(debtRecovered)}</strong></div>
                </div>
              </div>
              <div className="p-4 min-w-0">
                <h4 className="font-bold text-sm mb-3">Shop & payment</h4>
                <div className="space-y-1.5 text-xs">
                  <div className="flex justify-between"><span>Merchant order total</span><strong>{pence(order.totalPence)}</strong></div>
                  <div className="flex justify-between"><span>Shop gross before Stripe</span><strong>{order.storeGrossProceedsPence == null ? "Not snapshotted" : pence(order.storeGrossProceedsPence)}</strong></div>
                  <div className="flex justify-between"><span>Ledger recovery deducted</span><strong>-{pence(debtRecovered)}</strong></div>
                  <div className="flex justify-between"><span>Payment method</span><strong className="capitalize">{order.paymentMethod}</strong></div>
                  <div><span className="text-slate-500">Stripe payment:</span><p className="font-mono break-all mt-0.5">{order.stripePaymentIntentId ?? "None"}</p></div>
                  {order.ledgerTransactions.map((transaction) => (
                    <div key={transaction.id} className="border-t pt-1.5">
                      <span className="font-semibold">Ledger #{transaction.id}: {transaction.kind}</span>
                      <p className="break-all">{transaction.status}{transaction.stripeSessionId ? ` · ${transaction.stripeSessionId}` : ""}</p>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Timers (live orders only) */}
        {isLive && (
          <div className="flex flex-wrap gap-2">
            <TimerBadge isoString={order.createdAt} label="Order age" />
            {order.statusChangedAt && order.statusChangedAt !== order.createdAt && (
              <TimerBadge isoString={order.statusChangedAt} label={`In ${STATUS_LABEL[order.status] ?? order.status}`} />
            )}
            {order.claimedAt && <TimerBadge isoString={order.claimedAt} label="Driver assigned" />}
            {order.pickedUpAt && <TimerBadge isoString={order.pickedUpAt} label="Picked up" />}
          </div>
        )}

        {/* Alerts */}
        {urgentWait && (
          <div className="flex items-center gap-2 bg-red-50 border border-red-200 rounded-xl px-3 py-2">
            <AlertTriangle size={14} className="text-red-600 flex-shrink-0" />
            <p className="text-sm font-medium text-red-700">Waiting for driver — {inCurrentStatus.label} in Ready state</p>
          </div>
        )}
        {waitingForDriver && !urgentWait && (
          <div className="flex items-center gap-2 bg-amber-50 dark:bg-amber-900/20 border border-amber-200 rounded-xl px-3 py-2">
            <span className="w-2 h-2 rounded-full bg-amber-400 animate-pulse flex-shrink-0" />
            <p className="text-sm font-medium text-amber-700">Waiting for a driver to claim</p>
          </div>
        )}
        {(order.shopCallAttempts ?? 0) > 0 && (
          <div className="flex items-center gap-2 bg-violet-50 border border-violet-200 rounded-xl px-3 py-2">
            <PhoneCall size={14} className="text-violet-600 flex-shrink-0" />
            <p className="text-sm font-medium text-violet-700">
              System phoned the shop {order.shopCallAttempts}× — not accepted yet
              {order.shopCallBackupCalled ? " · manager backup called" : ""}
            </p>
          </div>
        )}

        {/* Audit trail — its own row rather than the action cluster below, so the
            tap target can be a full 44px on a phone without stretching the row
            of small text actions it would otherwise sit in. */}
        <div>
          <button
            onClick={() => toggleTimeline(order.id)}
            aria-expanded={timelineOpen}
            className={`w-full sm:w-auto min-h-[44px] flex items-center justify-center sm:justify-start gap-1.5 text-xs font-semibold px-3 rounded-xl border transition-colors ${
              timelineOpen
                ? "bg-slate-100 text-slate-700 border-slate-300"
                : "text-slate-600 border-slate-200 hover:bg-slate-50"
            }`}
          >
            <History size={13} />
            {timelineOpen ? "Hide audit trail" : "Audit trail"}
            <ChevronDown size={13} className={`transition-transform ${timelineOpen ? "rotate-180" : ""}`} />
          </button>
          {timelineOpen && (
            <div className="mt-2">
              <OrderTimeline
                state={timelines[order.id] ?? { phase: "loading" }}
                onRetry={() => void loadTimeline(order.id)}
              />
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="flex flex-wrap items-center gap-3 text-xs text-slate-500 pt-1 border-t border-slate-50">
          {order.scheduledFor && (
            <span className="px-2 py-0.5 rounded-full font-semibold bg-amber-50 dark:bg-amber-900/20 text-amber-700 border border-amber-200">
              🕐 Scheduled: {new Date(order.scheduledFor).toLocaleString("en-GB")}
            </span>
          )}
          {order.customerName && <span>Customer: <strong className="text-slate-700">{order.customerName}</strong></span>}
          {/* How well does the shop know this person? A returning customer with
              real lifetime value is worth handling differently from a first-timer. */}
          {order.customerIsReturning ? (
            <span className="relative inline-block">
              <button
                onClick={() => {
                  const opening = historyFor !== order.id;
                  setHistoryFor(opening ? order.id : null);
                  if (opening) void loadCustomerHistory(order.id);
                }}
                className="px-2 py-0.5 rounded-full font-semibold bg-emerald-50 dark:bg-emerald-900/20 text-emerald-700 border border-emerald-200 hover:bg-emerald-100 transition-colors"
                title="See this customer's previous orders"
                aria-expanded={historyFor === order.id}
              >
                ★ Returning · {order.customerPreviousOrders} before · £{((order.customerTotalSpentPence ?? 0) / 100).toFixed(2)}
              </button>

              {historyFor === order.id && (
                <>
                  {/* Click-away. Sits behind the panel so the panel stays clickable. */}
                  <div className="fixed inset-0 z-40" onClick={() => setHistoryFor(null)} />
                  <div className="absolute left-0 top-full mt-1 z-50 w-[22rem] max-h-80 overflow-auto rounded-xl border bg-card shadow-lg p-1 text-left">
                    <div className="px-3 py-2 text-xs font-semibold text-muted-foreground border-b">
                      {order.customerName}'s previous orders
                    </div>
                    {histories[order.id]?.loading && <div className="px-3 py-3 text-xs text-muted-foreground">Loading…</div>}
                    {histories[order.id]?.error && <div className="px-3 py-3 text-xs text-red-600">{histories[order.id]!.error}</div>}
                    {histories[order.id]?.orders?.length === 0 && <div className="px-3 py-3 text-xs text-muted-foreground">No previous orders.</div>}
                    {histories[order.id]?.orders?.map((h) => (
                      <button
                        key={h.id}
                        onClick={() => openOrderById(h.id)}
                        className="w-full flex items-center justify-between gap-3 px-3 py-2 rounded-lg hover:bg-muted transition-colors"
                        title={`Open order #${h.id}`}
                      >
                        <span className="min-w-0 text-left">
                          <span className="block text-xs font-semibold text-slate-700 truncate">{h.storeName ?? "—"}</span>
                          <span className="block text-[11px] text-muted-foreground">
                            {new Date(h.createdAt).toLocaleString("en-GB", { day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit" })}
                            {" · #"}{h.id}
                          </span>
                        </span>
                        <span className="shrink-0 text-sm font-bold text-emerald-700 underline decoration-dotted">
                          £{(h.customerPaidPence / 100).toFixed(2)}
                        </span>
                      </button>
                    ))}
                  </div>
                </>
              )}
            </span>
          ) : order.customerName ? (
            <span className="px-2 py-0.5 rounded-full font-semibold bg-sky-50 dark:bg-sky-900/20 text-sky-700 border border-sky-200">
              New customer
            </span>
          ) : null}
          {order.customerPhone && <span>{order.customerPhone}</span>}
          {order.deliveryAddress && <span className="truncate max-w-xs">📍 {order.deliveryAddress}</span>}
          {order.driverName && <span>Driver: <strong className="text-slate-700">{order.driverName}</strong></span>}
          {order.deliveryPhotoUrl && (
            <a href={order.deliveryPhotoUrl} target="_blank" rel="noopener noreferrer" title="Proof of delivery photo" className="inline-flex items-center gap-1 text-blue-600 hover:underline">
              <img src={order.deliveryPhotoUrl} alt="Proof of delivery" className="w-8 h-8 rounded object-cover border border-slate-200" /> Proof
            </a>
          )}
          {isHubDelivery && order.deliveryCode && <span className="font-mono text-xs bg-slate-100 border border-slate-200 px-2 py-0.5 rounded font-semibold text-slate-700">Code: {order.deliveryCode}</span>}
          <span>{fmtDateTime(order.createdAt)}</span>
          <span className={`px-2 py-0.5 rounded-full font-semibold ${order.paymentMethod === "card" ? "bg-blue-50 dark:bg-blue-900/20 text-blue-700" : "bg-slate-100 text-slate-600"}`}>
            {order.paymentMethod === "card" ? "Card" : "Cash"}
          </span>
          {(order.refundedPence ?? 0) > 0 && (
            <span className="px-2 py-0.5 rounded-full font-semibold bg-amber-50 dark:bg-amber-900/20 text-amber-700">
              Refunded {pence(order.refundedPence!)}
              {order.refundedAt && ` · ${fmtDateTime(order.refundedAt)}`}
            </span>
          )}
          <div className="w-full sm:w-auto sm:ml-auto flex flex-wrap items-center gap-2">
            {isHubDelivery && !!order.assignmentId && isLive && (
              <button
                onClick={() => setTrackingIds(prev => { const next = new Set(prev); if (next.has(order.id)) next.delete(order.id); else next.add(order.id); return next; })}
                className={`flex items-center gap-1.5 text-xs font-semibold px-3 py-1.5 rounded-lg border transition-colors ${trackingIds.has(order.id) ? "bg-blue-600 text-white border-blue-600" : "text-blue-600 border-blue-200 hover:bg-blue-50"}`}
              >
                <MapPin size={11} /> {trackingIds.has(order.id) ? "Hide Map" : "Track Driver"}
              </button>
            )}
            {isHubDelivery && isLive && (order.dispatchStatus === "failed" || order.dispatchStatus === "waiting_for_drivers" || (!order.assignmentId && ["confirmed", "preparing", "ready"].includes(order.status))) && (
              <button
                onClick={() => void redispatch(order.id)}
                disabled={redispatchingId === order.id}
                className="flex items-center gap-1.5 text-xs font-semibold text-violet-600 hover:text-violet-700 border border-violet-200 hover:bg-violet-50 px-3 py-1.5 rounded-lg transition-colors disabled:opacity-40"
              >
                <Zap size={11} className={redispatchingId === order.id ? "animate-pulse" : ""} />
                {redispatchingId === order.id ? "Dispatching…" : "Re-dispatch"}
              </button>
            )}
            {isHubDelivery && isLive && !!order.assignmentId && !order.pickedUpAt && (
              <button
                onClick={() => void forcePickup(order.id)}
                disabled={forcePickupId === order.id}
                className="flex items-center gap-1.5 text-xs font-semibold text-orange-600 hover:text-orange-700 border border-orange-200 hover:bg-orange-50 px-3 py-1.5 rounded-lg transition-colors disabled:opacity-40"
                title="Mark as picked up — use when driver has the order but can't mark it from the app"
              >
                <Package size={11} />
                {forcePickupId === order.id ? "Updating…" : "Force Pickup"}
              </button>
            )}
            {canRefund && (
              <button
                onClick={() => openRefund(order)}
                className="flex items-center gap-1.5 text-xs font-semibold text-indigo-600 hover:text-indigo-700 border border-indigo-200 hover:bg-indigo-50 px-3 py-1.5 rounded-lg transition-colors"
              >
                <RotateCcw size={11} /> Refund
              </button>
            )}
            {isLive && (
              <button
                onClick={() => setAssignModal({ order, selectedDriverId: null, busy: false, error: null })}
                className="flex items-center gap-1.5 text-xs font-semibold text-emerald-600 hover:text-emerald-700 border border-emerald-200 hover:bg-emerald-50 px-3 py-1.5 rounded-lg transition-colors"
              >
                <UserPlus size={11} /> {order.assignmentId ? "Reassign Driver" : "Assign Driver"}
              </button>
            )}
            {isLive && !!order.assignmentId && (
              <button
                onClick={() => void unassignDriver(order)}
                disabled={unassigningId === order.id}
                className="flex items-center gap-1.5 text-xs font-semibold text-rose-600 hover:text-rose-700 border border-rose-200 hover:bg-rose-50 px-3 py-1.5 rounded-lg transition-colors disabled:opacity-40"
              >
                <UserMinus size={11} /> {unassigningId === order.id ? "Unassigning…" : "Unassign Driver"}
              </button>
            )}
            {isLive && !!order.customerId && (
              <button
                onClick={() => void openChat(order, "customer")}
                className="flex items-center gap-1.5 text-xs font-semibold text-sky-600 hover:text-sky-700 border border-sky-200 hover:bg-sky-50 px-3 py-1.5 rounded-lg transition-colors"
              >
                <MessageCircle size={11} /> Chat Customer
              </button>
            )}
            {isLive && !!order.driverId && !!order.assignmentId && (
              <button
                onClick={() => void openChat(order, "driver")}
                className="flex items-center gap-1.5 text-xs font-semibold text-sky-600 hover:text-sky-700 border border-sky-200 hover:bg-sky-50 px-3 py-1.5 rounded-lg transition-colors"
              >
                <MessageCircle size={11} /> Chat Driver
              </button>
            )}
            {isLive && !!order.storeOwnerId && (
              <button
                onClick={() => void openChat(order, "shop")}
                className="flex items-center gap-1.5 text-xs font-semibold text-sky-600 hover:text-sky-700 border border-sky-200 hover:bg-sky-50 px-3 py-1.5 rounded-lg transition-colors"
              >
                <MessageCircle size={11} /> Chat Shop
              </button>
            )}
            {isLive && (
              <button
                onClick={() => void openSpy(order)}
                className="flex items-center gap-1.5 text-xs font-semibold text-violet-600 hover:text-violet-700 border border-violet-200 hover:bg-violet-50 px-3 py-1.5 rounded-lg transition-colors"
              >
                <Eye size={11} /> Spy chats
              </button>
            )}
            {isLive && order.driverMode === "hub" && order.fulfilmentType === "delivery" && !order.isMultiOrderChild && !(order.leg2DeliveryFeePence ?? 0) && (
              <div className="flex flex-wrap items-center gap-1.5">
                <span className="flex items-center gap-1 text-xs font-semibold text-slate-500">
                  <Banknote size={11} /> Offer £{((order.driverOfferPence) / 100).toFixed(2)}
                </span>
                {[50, 100, 200].map(p => (
                  <button key={p} onClick={() => void bumpDriverOffer(order, p)} disabled={bumpingId !== null || order.driverOfferPence >= 5000}
                    title="Ayrshire Hub pays this increase; the customer and shop pay no extra"
                    className="text-xs font-semibold text-amber-700 border border-amber-200 hover:bg-amber-50 px-2 py-1.5 rounded-lg transition-colors disabled:opacity-40">
                    {p === 50 ? "+50p" : `+£${p / 100}`}
                  </button>
                ))}
                <span className="text-xs text-slate-500">Hub pays increases</span>
              </div>
            )}
            {order.status === "pending" && (
              <button
                onClick={() => void acceptOrder(order)}
                disabled={advancingId === order.id}
                className="flex items-center gap-1.5 text-xs font-semibold text-white bg-emerald-600 hover:bg-emerald-700 px-3 py-1.5 rounded-lg transition-colors disabled:opacity-40"
              >
                <CheckCheck size={11} /> {advancingId === order.id ? "Accepting…" : "Accept Order"}
              </button>
            )}
            {isLive && order.status !== "out_for_delivery" && order.status !== "cancelled" && (
              <button
                onClick={() => void advanceStatus(order, "out_for_delivery")}
                disabled={advancingId === order.id}
                className="flex items-center gap-1.5 text-xs font-semibold text-blue-600 hover:text-blue-700 border border-blue-200 hover:bg-blue-50 dark:bg-blue-900/20 px-3 py-1.5 rounded-lg transition-colors disabled:opacity-40"
              >
                <Truck size={11} /> {advancingId === order.id ? "Updating…" : "Out for Delivery"}
              </button>
            )}
            {isLive && order.status !== "cancelled" && (
              <button
                onClick={() => void advanceStatus(order, "delivered")}
                disabled={advancingId === order.id}
                className="flex items-center gap-1.5 text-xs font-semibold text-emerald-600 hover:text-emerald-700 border border-emerald-200 hover:bg-emerald-50 px-3 py-1.5 rounded-lg transition-colors disabled:opacity-40"
              >
                <CheckCheck size={11} /> {advancingId === order.id ? "Updating…" : "Mark Completed"}
              </button>
            )}
            {isLive && order.status !== "cancelled" && (
              <button
                onClick={() => void cancelOrder(order)}
                disabled={cancellingId === order.id}
                className="flex items-center gap-1.5 text-xs font-semibold text-red-600 hover:text-red-700 border border-red-200 hover:bg-red-50 px-3 py-1.5 rounded-lg transition-colors disabled:opacity-40"
              >
                <XCircle size={11} /> {cancellingId === order.id ? "Cancelling…" : "Cancel Order"}
              </button>
            )}
          </div>
        </div>

        {trackingIds.has(order.id) && (
          <DriverTrackingPanel
            orderId={order.id}
            storeLat={order.storeLatitude ?? null}
            storeLng={order.storeLongitude ?? null}
          />
        )}
      </div>
    );
  }

  const byStatus = Object.fromEntries(
    LIVE_STATUSES.map(s => [s, orders.filter(o => o.status === s).length])
  );
  // Delivery runs are jobs too — surface them in the live summary so the view never looks
  // empty just because there are no live FOOD orders (a run with drops is still live work).
  const activeRunCount = runs.length;
  const activeRunDrops = runs.reduce((s, r) => s + (r.dropCount ?? 0), 0);
  const totalPages = Math.ceil(total / PAGE_SIZE);

  const tabs: { key: Tab; label: string }[] = [
    { key: "live", label: "Live Orders" },
    { key: "completed", label: "Completed" },
    { key: "cancelled", label: "Cancelled" },
    { key: "all", label: "All Orders" },
  ];

  // Build grouped order display: multi-order siblings rendered together
  function buildGroups() {
    const seen = new Set<number>();
    const groups: Array<{ key: string; orders: Order[]; isMulti: boolean }> = [];
    for (const order of orders) {
      if (seen.has(order.id)) continue;
      seen.add(order.id);
      if (order.multiOrderId) {
        const siblings = orders.filter(
          (o) => o.multiOrderId === order.multiOrderId && o.id !== order.id && !seen.has(o.id)
        );
        siblings.forEach((s) => seen.add(s.id));
        groups.push({ key: `multi-${order.multiOrderId}`, orders: [order, ...siblings], isMulti: true });
      } else {
        groups.push({ key: `order-${order.id}`, orders: [order], isMulti: false });
      }
    }
    return groups;
  }

  const runSortMs = (run: typeof runs[number]) => Date.parse(run.completedAt ?? run.createdAt) || 0;

  const renderRunCard = (run: typeof runs[number]) => {
    const expanded = expandedRunIds.has(run.id);
    const runTotal = run.dropFeesTotalPence + run.fuelSupplementPence + run.hubFeePence;
    return (
      <div id={`admin-run-${run.id}`} key={`run-${run.id}`} className="bg-white rounded-2xl border border-slate-100 p-4 space-y-3">
        <div className="flex items-start justify-between gap-3 flex-wrap">
          <div className="flex items-center gap-2 flex-wrap">
            <span className="font-bold text-foreground text-base">Job #{run.id}</span>
            <span className="font-semibold text-slate-700 text-sm">{run.storeName ?? "Unknown store"}</span>
            <span className={`text-xs font-semibold px-2 py-0.5 rounded-full ${runStatusClass(run.status)}`}>{runStatusLabel(run.status)}</span>
            <span className="text-xs text-slate-400 flex items-center gap-1"><Truck size={10} /> {run.deliveredCount}/{run.dropCount} drops delivered</span>
            <span className="text-xs text-slate-500">· {run.completedAt ? `Completed ${fmtDateTime(run.completedAt)}` : `Started ${fmtDateTime(run.createdAt)}`}</span>
          </div>
          <div className="flex items-center gap-2">
            <span className="font-bold text-slate-800">{pence(runTotal)}</span>
            <button onClick={() => setExpandedRunIds((prev) => { const next = new Set(prev); if (next.has(run.id)) next.delete(run.id); else next.add(run.id); return next; })} className="p-1.5 rounded-lg border border-slate-200 hover:bg-slate-50" title="View run + delivery proof">
              <ChevronDown size={15} className={`transition-transform ${expanded ? "rotate-180" : ""}`} />
            </button>
          </div>
        </div>
        {run.driverName&&<RunContactActions run={run} headers={authHeaders()}/>}
        <RunHistory runId={run.id} headers={authHeaders()} admin/>
        <RunJobActions run={run} headers={authHeaders()} admin onChanged={()=>fetchOrders(tab,page,search,true)}/>
        {expanded && (
          <div className="rounded-xl border border-slate-200 bg-slate-50/60 p-3 space-y-3">
            <div className="flex justify-between items-center text-sm">
              <span className="text-slate-600">{run.driverName ? `Driver: ${run.driverName}` : "Awaiting a driver"}</span>
              <span className="text-xs text-slate-400">Multidrop run · {run.dropCount} drops</span>
            </div>
            {run.runNote && <p className="text-xs text-amber-900 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2">📝 {run.runNote}</p>}
            <div className="grid sm:grid-cols-2 gap-3 text-sm">
              <div className="bg-white rounded-lg border border-slate-200 p-3">
                <p className="text-xs font-semibold text-slate-500 uppercase tracking-wide mb-2">Driver earnings</p>
                <div className="flex justify-between py-0.5"><span className="text-slate-500">Drop fees (base pay)</span><span className="text-slate-800">{pence(run.dropFeesTotalPence)}</span></div>
                <div className="flex justify-between py-0.5"><span className="text-slate-500">Fuel / bonus</span><span className="text-slate-800">{pence(run.fuelSupplementPence)}</span></div>
                <div className="flex justify-between font-semibold text-slate-900 border-t border-slate-100 pt-1 mt-1"><span>Total driver earnings</span><span>{pence(run.dropFeesTotalPence + run.fuelSupplementPence)}</span></div>
              </div>
              <div className="bg-white rounded-lg border border-slate-200 p-3">
                <p className="text-xs font-semibold text-slate-500 uppercase tracking-wide mb-2">Hub &amp; shop</p>
                <div className="flex justify-between py-0.5"><span className="text-slate-500">Hub fee collected</span><span className="text-slate-800">{pence(run.hubFeePence)}</span></div>
                <div className="flex justify-between font-semibold text-slate-900 border-t border-slate-100 pt-1 mt-1"><span>Shop pays (total)</span><span>{pence(runTotal)}</span></div>
                <div className="flex justify-between text-xs text-slate-400 pt-1"><span>Payment</span><span>Shop credit (from proceeds)</span></div>
              </div>
            </div>
            <p className="text-xs font-semibold text-slate-500 uppercase tracking-wide">Drops &amp; delivery proof</p>
            <div className="space-y-2">
              {(run.stops ?? []).map((stop) => (
                <div key={stop.id} className="flex items-center gap-3 bg-white rounded-lg border border-slate-200 p-2">
                  <div className={`w-7 h-7 rounded-md flex items-center justify-center text-xs font-bold shrink-0 ${stop.status === "delivered" ? "bg-emerald-600 text-white" : "bg-slate-200 text-slate-600"}`}>{stop.sequence + 1}</div>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-slate-800 truncate">{stop.areaLabel || stop.addressText || stop.postcode || "Drop"}</p>
                    <p className="text-xs text-slate-400">{stop.status === "delivered" ? "Delivered" : stop.status === "failed" ? "Failed" : "Pending"} · {pence(stop.feePence)}{stop.deliveredAt ? ` · ${fmtDateTime(stop.deliveredAt)}` : ""}</p>
                    {stop.deliveredLat != null && stop.deliveredLng != null && (
                      <a href={`https://www.google.com/maps/search/?api=1&query=${stop.deliveredLat},${stop.deliveredLng}`} target="_blank" rel="noopener noreferrer" className="text-xs text-blue-600 hover:underline inline-flex items-center gap-1" title="Where the driver marked this drop delivered">📍 View drop location</a>
                    )}
                  </div>
                  {stop.deliveryPhotoUrl ? (
                    <a href={stop.deliveryPhotoUrl} target="_blank" rel="noopener noreferrer" title="Open proof photo">
                      <img src={stop.deliveryPhotoUrl} alt={`Proof of delivery for drop ${stop.sequence + 1}`} className="w-14 h-14 rounded-md object-cover border border-slate-200" />
                    </a>
                  ) : (<span className="text-xs text-slate-300">No photo</span>)}
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    );
  };

  return (
    <AdminLayout current="/admin/orders">
      <div className="space-y-5">

        {/* Header */}
        <div className="flex items-center justify-between flex-wrap gap-3">
          <div>
            <h1 className="text-2xl font-bold text-foreground">Orders</h1>
            <p className="text-sm text-slate-500 mt-0.5">
              {tab === "live"
                ? `${total} active order${total !== 1 ? "s" : ""} across all stores · auto-refreshes every 15s`
                : `${total} order${total !== 1 ? "s" : ""}`}
            </p>
          </div>
          <div className="flex items-center gap-3">
            <span className="text-xs text-slate-400">
              {lastRefresh.toLocaleTimeString("en-GB", { hour: "2-digit", minute: "2-digit", second: "2-digit" })}
            </span>
            <button
              onClick={() => void fetchOrders(tab, page, search)}
              className="flex items-center gap-1.5 text-xs font-medium text-slate-600 hover:text-foreground bg-white border border-slate-200 rounded-lg px-3 py-1.5 transition-colors"
            >
              <RefreshCw size={12} /> Refresh
            </button>
          </div>
        </div>

        <AdminDeliveryBundles onOpenOrder={openOrderById} onOpenRun={(id)=>{setExpandedRunIds(prev=>new Set([...prev,id]));setTimeout(()=>{const node=document.getElementById(`admin-run-${id}`);if(node)node.scrollIntoView({behavior:"smooth",block:"start"});else window.location.assign(`/admin/delivery-runs?runId=${id}`);},100);}} onChanged={()=>void fetchOrders(tab,page,search)}/>

        {/* Order stats — today / this week / last week */}
        {orderStats && (
          <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
            {([
              { label: "Today",     data: orderStats.today },
              { label: "This week", data: orderStats.thisWeek },
              { label: "Last week", data: orderStats.lastWeek },
            ] as const).map(({ label, data }) => (
              <div key={label} className="bg-white dark:bg-card rounded-xl border border-slate-100 dark:border-border p-4">
                <p className="text-xs font-semibold text-slate-400 uppercase tracking-wide mb-2">{label}</p>
                <div className="flex items-end justify-between gap-2">
                  <div>
                    <p className="text-3xl font-extrabold text-foreground leading-none">{data.total}</p>
                    <p className="text-xs text-slate-400 mt-0.5">total orders</p>
                  </div>
                  {data.cancelled > 0 && (
                    <div className="text-right">
                      <p className="text-lg font-bold text-red-500 leading-none">{data.cancelled}</p>
                      <p className="text-xs text-slate-400 mt-0.5">cancelled</p>
                    </div>
                  )}
                  {data.cancelled === 0 && (
                    <span className="text-xs font-semibold text-green-600 bg-green-50 px-2 py-0.5 rounded-full">0 cancelled</span>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Tabs */}
        <div className="flex flex-wrap gap-1 bg-slate-100 rounded-xl p-1 w-fit max-w-full">
          {tabs.map(t => (
            <button
              key={t.key}
              onClick={() => switchTab(t.key)}
              className={`px-4 py-2 rounded-lg text-sm font-semibold transition-colors ${
                tab === t.key ? "bg-white text-foreground shadow-sm" : "text-slate-500 hover:text-slate-700"
              }`}
            >
              {t.label}
            </button>
          ))}
        </div>

        {/* Live summary stats */}
        {tab === "live" && (
          <div className="grid grid-cols-2 sm:grid-cols-6 gap-3">
            {LIVE_STATUSES.map(s => (
              <div key={s} className="bg-white rounded-xl border border-slate-100 p-3 text-center">
                <p className="text-2xl font-bold text-foreground">{byStatus[s] ?? 0}</p>
                <p className="text-xs text-slate-500 mt-0.5">{STATUS_LABEL[s]}</p>
              </div>
            ))}
            <div className="bg-white rounded-xl border border-slate-100 p-3 text-center">
              <p className="text-2xl font-bold text-foreground">{activeRunCount}</p>
              <p className="text-xs text-slate-500 mt-0.5">Delivery runs{activeRunDrops > 0 ? ` · ${activeRunDrops} drops` : ""}</p>
            </div>
          </div>
        )}

        {/* Search (completed + all tabs) */}
        {tab !== "live" && (
          <form onSubmit={handleSearch} className="flex gap-2 max-w-md">
            <div className="relative flex-1">
              <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
              <input
                value={searchInput}
                onChange={e => setSearchInput(e.target.value)}
                placeholder="Search by name or order #…"
                className="w-full pl-8 pr-3 py-2 text-sm border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-300"
              />
            </div>
            <button
              type="submit"
              className="px-4 py-2 text-sm font-semibold bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition-colors"
            >
              Search
            </button>
          </form>
        )}


        {/* Order list */}
        {loadError ? (
          <div className="bg-red-50 rounded-2xl border border-red-200 p-8 flex flex-col items-center gap-3 text-red-700">
            <AlertTriangle size={32} className="opacity-70" />
            <p className="font-semibold">{loadError}</p>
            <button
              onClick={() => void fetchOrders(tab, page, search)}
              className="px-4 py-2 text-sm font-semibold bg-white border border-red-200 rounded-lg hover:bg-red-100 transition-colors"
            >
              Try again
            </button>
          </div>
        ) : loading ? (
          <div className="flex justify-center py-20">
            <div className="w-8 h-8 rounded-full border-2 border-primary border-t-transparent animate-spin" />
          </div>
        ) : orders.length === 0 && runs.length === 0 ? (
          <div className="bg-white rounded-2xl border border-slate-100 p-16 flex flex-col items-center gap-3 text-slate-400">
            <CheckCircle2 size={40} className="opacity-30" />
            <p className="font-medium">{tab === "live" ? "No active orders right now" : "No orders found"}</p>
            {tab === "live" && <p className="text-sm">All caught up!</p>}
          </div>
        ) : (
          <div className="space-y-3">
            {[...buildGroups().map((group) => {
              const sortMs = Date.parse(group.orders[0].createdAt) || 0;
              if (!group.isMulti) {
                return { sortMs, node: renderOrderCard(group.orders[0]) };
              }
              // Multi-order group — teal left bar connects siblings visually
              return { sortMs, node: (
                <div key={group.key} className="relative pl-5">
                  {/* Teal vertical bar spanning the whole group */}
                  <div className="absolute left-0 top-0 bottom-0 w-1.5 rounded-full bg-teal-500" />
                  {/* Badge above the group */}
                  <div className="mb-2 flex items-center gap-2">
                    <div className="flex items-center gap-1.5 bg-teal-600 text-white text-xs font-bold px-2.5 py-1 rounded-full">
                      <Layers size={11} />
                      MULTI-STORE ORDER #{group.orders[0].multiOrderId}
                    </div>
                    <span className="text-xs text-slate-500">{group.orders.length} stores · delivered together by one driver</span>
                  </div>
                  {/* Sibling cards */}
                  <div className="space-y-2">
                    {group.orders.map((order, idx) => (
                      <div key={order.id}>
                        <div className="mb-1 text-xs text-teal-700 font-semibold">Stop {idx + 1} of {group.orders.length}</div>
                        {renderOrderCard(order)}
                      </div>
                    ))}
                    {/* Shared delivery fee summary */}
                    {(() => {
                      const deliveryFee = group.orders[0].multiOrderDeliveryFeePence ?? 0;
                      const storeSubtotals = group.orders.reduce((sum, o) =>
                        sum + o.subtotalPence + (o.serviceChargePence ?? 0) + (o.bagChargePence ?? 0)
                          + (o.customerPlatformFeePence ?? 0) + (o.customerHubDriverFeePence ?? 0)
                          - o.offerDiscountPence, 0);
                      const combinedTotal = storeSubtotals + deliveryFee;
                      return (
                        <div className="bg-teal-50 border border-teal-200 rounded-xl px-4 py-3 text-sm space-y-1">
                          <div className="flex justify-between text-teal-700">
                            <span>Store items + fees combined</span>
                            <strong>{pence(storeSubtotals)}</strong>
                          </div>
                          {deliveryFee > 0 && (
                            <div className="flex justify-between text-teal-700">
                              <span>Shared delivery (one driver, all stores)</span>
                              <strong>{pence(deliveryFee)}</strong>
                            </div>
                          )}
                          <div className="flex justify-between font-bold text-teal-900 border-t border-teal-200 pt-1">
                            <span>Total customer paid (combined)</span>
                            <span>{pence(combinedTotal)}</span>
                          </div>
                        </div>
                      );
                    })()}
                  </div>
                </div>
              ) };
            }), ...runs.map((run) => ({ sortMs: runSortMs(run), node: renderRunCard(run) }))]
              .sort((a, b) => b.sortMs - a.sortMs)
              .map((it) => it.node)}
          </div>
        )}

        {/* Pagination */}
        {totalPages > 1 && (
          <div className="flex items-center justify-center gap-3 pt-2">
            <button
              disabled={page <= 1}
              onClick={() => { latestRequestRef.current += 1; setPage(p => p - 1); }}
              className="p-2 rounded-lg border border-slate-200 disabled:opacity-40 hover:bg-slate-50 transition-colors"
            >
              <ChevronLeft size={16} />
            </button>
            <span className="text-sm text-slate-600 font-medium">Page {page} of {totalPages}</span>
            <button
              disabled={page >= totalPages}
              onClick={() => { latestRequestRef.current += 1; setPage(p => p + 1); }}
              className="p-2 rounded-lg border border-slate-200 disabled:opacity-40 hover:bg-slate-50 transition-colors"
            >
              <ChevronRight size={16} />
            </button>
          </div>
        )}
      </div>

      {/* Assign driver modal */}
      {assignModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/40" onClick={() => !assignModal.busy && setAssignModal(null)}>
          <div className="bg-white rounded-2xl shadow-xl w-full max-w-sm p-6 space-y-4" onClick={e => e.stopPropagation()}>
            <div className="flex items-center justify-between">
              <h2 className="text-lg font-bold text-foreground">
                {assignModal.order.assignmentId ? "Reassign Driver" : "Assign Driver"} — Order #{assignModal.order.id}
              </h2>
              {!assignModal.busy && (
                <button onClick={() => setAssignModal(null)} className="text-slate-400 hover:text-slate-600 text-xl leading-none">×</button>
              )}
            </div>
            {assignModal.order.driverName && (
              <p className="text-sm text-slate-500">Current driver: <strong className="text-slate-700">{assignModal.order.driverName}</strong></p>
            )}
            <div>
              <label className="text-xs font-medium text-slate-600 mb-1 block">Select driver</label>
              <select
                value={assignModal.selectedDriverId ?? ""}
                onChange={e => setAssignModal(m => m && { ...m, selectedDriverId: Number(e.target.value) || null })}
                className="w-full border border-slate-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-300"
              >
                <option value="">— Choose a driver —</option>
                {drivers.map(d => (
                  <option key={d.userId} value={d.userId}>
                    {d.userName ?? `Driver #${d.userId}`}{d.isOnline ? " 🟢" : " ⚫"}
                  </option>
                ))}
              </select>
            </div>
            {assignModal.error && (
              <div className="bg-red-50 border border-red-200 rounded-xl p-3 text-sm text-red-700">{assignModal.error}</div>
            )}
            <button
              onClick={() => void submitAssign()}
              disabled={assignModal.busy || !assignModal.selectedDriverId}
              className="w-full py-2.5 bg-emerald-600 hover:bg-emerald-700 disabled:opacity-50 text-white font-semibold rounded-xl transition-colors"
            >
              {assignModal.busy ? "Assigning…" : assignModal.order.assignmentId ? "Reassign Driver" : "Assign Driver"}
            </button>
          </div>
        </div>
      )}

      {/* Chat with driver modal */}
      {chatModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/40" onClick={() => !chatModal.sending && setChatModal(null)}>
          <div className="bg-white rounded-2xl shadow-xl w-full max-w-md flex flex-col" style={{ maxHeight: "80vh" }} onClick={e => e.stopPropagation()}>
            <div className="flex items-center justify-between p-5 border-b border-slate-100">
              <div>
                <h2 className="text-base font-bold text-foreground">Chat — Order #{chatModal.order.id}</h2>
                <p className="text-xs text-slate-500 mt-0.5">{chatModal.title}{chatModal.recipientUserId ? "" : " — not reachable"}</p>
              </div>
              <button onClick={() => setChatModal(null)} className="text-slate-400 hover:text-slate-600 text-xl leading-none">×</button>
            </div>

            <div className="flex-1 overflow-y-auto p-4 space-y-3 min-h-0">
              {chatModal.busy ? (
                <div className="flex justify-center py-8">
                  <div className="w-6 h-6 rounded-full border-2 border-sky-400 border-t-transparent animate-spin" />
                </div>
              ) : chatModal.messages.length === 0 ? (
                <p className="text-center text-sm text-slate-400 py-8">No messages yet</p>
              ) : (
                chatModal.messages.map(msg => (
                  <div key={msg.id} className="space-y-0.5">
                    <p className="text-xs text-slate-400">{msg.senderName ?? "Unknown"} · {new Date(msg.createdAt).toLocaleTimeString("en-GB", { hour: "2-digit", minute: "2-digit" })}</p>
                    <div className="bg-slate-100 rounded-xl px-3 py-2 text-sm text-slate-800 w-fit max-w-xs"><MessageImages ids={msg.attachmentIds}/>{msg.body}</div>
                  </div>
                ))
              )}
              <div ref={chatBottomRef} />
            </div>

            {chatModal.error && (
              <div className="mx-4 mb-2 bg-red-50 border border-red-200 rounded-xl p-3 text-xs text-red-700">{chatModal.error}</div>
            )}

            <div className="p-4 border-t border-slate-100 flex gap-2">
              {chatModal.recipientUserId&&<ChatImageComposer key={`${chatModal.order.id}:${chatModal.recipientUserId}`} endpoint={`/api/orders/${chatModal.order.id}/driver-messages`} recipientUserId={chatModal.recipientUserId} onSent={async()=>{const r=await fetch(`/api/orders/${chatModal.order.id}/driver-messages`,{headers:authHeaders()});const d=await r.json();setChatModal(m=>m&&({...m,messages:scopeThread(d.items,m.recipientUserId!)}));}}/>}
<input
                value={chatModal.input}
                onChange={e => setChatModal(m => m && { ...m, input: e.target.value })}
                onKeyDown={e => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); void sendChatMessage(); } }}
                placeholder={chatModal.recipientUserId ? `Message the ${chatModal.target}…` : "This party isn't on the order"}
                disabled={chatModal.sending || chatModal.busy || !chatModal.recipientUserId}
                className="flex-1 border border-slate-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-sky-300 disabled:opacity-50"
              />
              <button
                onClick={() => void sendChatMessage()}
                disabled={chatModal.sending || chatModal.busy || !chatModal.input.trim() || !chatModal.recipientUserId}
                className="flex items-center gap-1.5 px-3 py-2 bg-sky-600 hover:bg-sky-700 disabled:opacity-40 text-white rounded-lg text-sm font-semibold transition-colors"
              >
                <Send size={13} /> {chatModal.sending ? "…" : "Send"}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Spy modal — read-only view of both order conversations */}
      {spyModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/40" onClick={() => setSpyModal(null)}>
          <div className="bg-card rounded-2xl shadow-xl w-full max-w-2xl max-h-[85vh] flex flex-col" onClick={e => e.stopPropagation()}>
            <div className="p-4 border-b border-border flex items-center justify-between">
              <div>
                <h2 className="text-base font-bold text-foreground flex items-center gap-1.5"><Eye size={15} /> Spy — Order #{spyModal.order.id}</h2>
                <p className="text-xs text-slate-500 mt-0.5">Read-only view of this order's conversations</p>
              </div>
              <button onClick={() => setSpyModal(null)} className="text-slate-400 hover:text-slate-600"><XCircle size={20} /></button>
            </div>
            <div className="flex-1 overflow-y-auto p-4 space-y-5">
              {spyModal.busy ? (
                <p className="text-center text-sm text-slate-400 py-8">Loading…</p>
              ) : spyModal.error ? (
                <p className="text-center text-sm text-red-600 py-8">{spyModal.error}</p>
              ) : (
                <>
                  <div>
                    <h3 className="text-xs font-bold uppercase tracking-wide text-slate-500 mb-2">Customer ↔ Driver</h3>
                    {spyModal.driver.length === 0 ? (
                      <p className="text-sm text-slate-400">No messages</p>
                    ) : spyModal.driver.map(m => (
                      <div key={m.id} className="mb-1.5 text-sm">
                        <span className="font-semibold text-slate-700">{m.senderName ?? `User ${m.senderUserId}`}:</span>{" "}
                        <div className="text-slate-600">{m.body}<MessageImages ids={m.attachmentIds}/></div>
                      </div>
                    ))}
                  </div>
                  <div>
                    <h3 className="text-xs font-bold uppercase tracking-wide text-slate-500 mb-2">Customer ↔ Shop</h3>
                    {spyModal.shop.length === 0 ? (
                      <p className="text-sm text-slate-400">No messages</p>
                    ) : spyModal.shop.map(m => (
                      <div key={m.id} className="mb-1.5 text-sm">
                        <span className="font-semibold text-slate-700 capitalize">{m.senderRole}:</span>{" "}
                        <div className="text-slate-600">{m.body}<MessageImages ids={m.attachmentIds}/></div>
                      </div>
                    ))}
                  </div>
                </>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Refund modal */}
      {refundModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/40" onClick={() => !refundModal.busy && setRefundModal(null)}>
          <div className="bg-white rounded-2xl shadow-xl w-full max-w-md p-6 space-y-4" onClick={e => e.stopPropagation()}>
            <div className="flex items-center justify-between">
              <h2 className="text-lg font-bold text-foreground">Refund Order #{refundModal.order.id}</h2>
              {!refundModal.busy && (
                <button onClick={() => setRefundModal(null)} className="text-slate-400 hover:text-slate-600 text-xl leading-none">×</button>
              )}
            </div>

            <div className="bg-slate-50 rounded-xl p-4 space-y-1 text-sm">
              <div className="flex justify-between">
                <span className="text-slate-500">Store</span>
                <span className="font-medium">{refundModal.order.storeName}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-slate-500">Customer</span>
                <span className="font-medium">{refundModal.order.customerName ?? "–"}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-slate-500">Order total</span>
                <span className="font-medium">{pence(refundModal.order.totalPence)}</span>
              </div>
              {(refundModal.order.refundedPence ?? 0) > 0 && (
                <div className="flex justify-between text-amber-600">
                  <span>Already refunded</span>
                  <span className="font-medium">–{pence(refundModal.order.refundedPence!)}</span>
                </div>
              )}
              <div className="flex justify-between font-semibold border-t border-slate-200 pt-1 mt-1">
                <span>Refundable</span>
                <span>{pence(refundModal.order.totalPence - (refundModal.order.refundedPence ?? 0))}</span>
              </div>
            </div>

            {refundModal.success ? (
              <div className="bg-green-50 border border-green-200 rounded-xl p-3 text-sm text-green-700 font-medium">{refundModal.success}</div>
            ) : (
              <>
                <div className="flex gap-2">
                  <button
                    onClick={() => {
                      const max = (refundModal.order.totalPence - (refundModal.order.refundedPence ?? 0)) / 100;
                      setRefundModal(m => m && { ...m, mode: "full", amountInput: max.toFixed(2) });
                    }}
                    className={`flex-1 py-2 rounded-lg text-sm font-semibold border transition-colors ${refundModal.mode === "full" ? "bg-indigo-600 text-white border-indigo-600" : "border-slate-200 text-slate-600 hover:bg-slate-50"}`}
                  >
                    Full Refund
                  </button>
                  <button
                    onClick={() => setRefundModal(m => m && { ...m, mode: "partial", amountInput: "" })}
                    className={`flex-1 py-2 rounded-lg text-sm font-semibold border transition-colors ${refundModal.mode === "partial" ? "bg-indigo-600 text-white border-indigo-600" : "border-slate-200 text-slate-600 hover:bg-slate-50"}`}
                  >
                    Partial Refund
                  </button>
                </div>

                {refundModal.mode === "partial" && (
                  <div>
                    <label className="text-xs font-medium text-slate-600 mb-1 block">Amount to refund (£)</label>
                    <input
                      type="number"
                      step="0.01"
                      min="0.01"
                      max={((refundModal.order.totalPence - (refundModal.order.refundedPence ?? 0)) / 100).toFixed(2)}
                      value={refundModal.amountInput}
                      onChange={e => setRefundModal(m => m && { ...m, amountInput: e.target.value })}
                      className="w-full border border-slate-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-300"
                      placeholder="0.00"
                    />
                  </div>
                )}

                {refundModal.error && (
                  <div className="bg-red-50 border border-red-200 rounded-xl p-3 text-sm text-red-700">{refundModal.error}</div>
                )}

                <button
                  onClick={() => void submitRefund()}
                  disabled={refundModal.busy}
                  className="w-full py-2.5 bg-indigo-600 hover:bg-indigo-700 disabled:opacity-50 text-white font-semibold rounded-xl transition-colors"
                >
                  {refundModal.busy
                    ? "Processing…"
                    : `Refund ${refundModal.mode === "full"
                        ? pence(refundModal.order.totalPence - (refundModal.order.refundedPence ?? 0))
                        : refundModal.amountInput ? `£${parseFloat(refundModal.amountInput).toFixed(2)}` : "…"
                      } via Stripe`}
                </button>
              </>
            )}
          </div>
        </div>
      )}
    </AdminLayout>
  );
}
